<template>
  <div
    ref="container"
    class="date-field"
    :class="{ 'date-field--readonly': readonly }"
  >
    <v-menu
      transition="slide-y-transition"
      :offset-y="offsetY"
      :offset-x="offsetX"
      :origin="origin"
      min-width="290px"
      :nudge-left="nudgeLeft"
      :width="width"
      :close-on-content-click="false"
      v-model="showMenu"
      :disabled="readonly || isDisabled"
      :attach="editable"
    >
      <template #activator="{ on, attrs }">
        <slot v-bind="{ on: { ...on, keydown: keydown } }">
          <v-btn
            v-if="btn"
            fab
            small
            v-bind="attrs"
            v-on="on"
            @keydown.up="up"
            @keydown.down="down"
            @keydown.left="left"
            @keydown.right="right"
            @keydown.enter="confirm"
          >
            <v-icon>fal fa-calendar-alt</v-icon>
          </v-btn>
          <v-text-field
            v-else
            :label="label"
            :readonly="!editable || range || readonly"
            :disabled="disabled"
            :value="formatedValue"
            @input="handleFieldInput"
            @blur="handleBlur"
            no-title
            scrollable
            v-on="on"
            v-bind="attrs"
            :persistent-hint="!!hint"
            :hint="hint"
            :autofocus="autofocus"
            :clearable="clearable"
            @click:clear="$emit('input', '')"
            :append-icon="appendIcon"
            @click:append="
              appendIcon !== 'fa-calendar'
                ? $emit('click:append', $event)
                : (showMenu = !clearable)
            "
            :outlined="outlined"
            :hide-details="hideDetails"
            :dense="dense"
            @update:error="$emit('update:error', $event)"
            :rules="[...(rules || []), dateFormat]"
            @keydown.up="up"
            @keydown.down="down"
            @keydown.left="left"
            @keydown.right="right"
            @keydown.enter="confirm"
            ref="field"
          >
            <template v-slot:append-outer>
              <slot name="append-outer"> </slot>
            </template>
            <template v-slot:append>
              <slot name="append"> </slot>
            </template>
          </v-text-field>
        </slot>
      </template>
      <v-date-picker
        class="date-picker"
        ref="datePicker"
        no-title
        scrollable
        :locale="language"
        :value="value"
        :range="range"
        :min="min"
        :max="max"
        :type="pickerType"
        :first-day-of-week="1"
        :picker-date="pickerDate"
        @update:picker-date="$emit('update:picker-date', $event)"
        tabindex="-1"
        :width="width"
        color="primary"
        @click.native="focusDatepicker"
        @keydown.native.up="up"
        @keydown.native.down="down"
        @keydown.native.left="left"
        @keydown.native.right="right"
        @keydown.native.enter="confirm"
        @click:date="dateSelected"
        @click:month="monthSelected"
        @click:year="yearSelected"
        @change="$emit('input', $event)"
      >
        <template v-if="range">
          <v-row>
            <v-col cols="6" md="6" sm="6" align="center">
              <v-btn
                :disabled="disableConfirm"
                color="primary"
                @click="confirmDatePicker"
              >
                {{ $t("Confirm") }}
              </v-btn>
            </v-col>
            <v-col cols="6" md="6" sm="6" align="center">
              <v-btn @click="closeDatePicker" color="error">
                {{ $t("Cancel") }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
        <template v-else-if="shouldBeConfirmed">
          <v-row>
            <v-col cols="6" md="6" sm="6" align="center">
              <v-btn
                :disabled="disableConfirmOnDatePicker"
                color="primary"
                @click="confirmDatePicker"
              >
                {{ $t("Confirm") }}
              </v-btn>
            </v-col>
            <v-col cols="6" md="6" sm="6" align="center">
              <v-btn @click="closeDatePicker" color="error">
                {{ $t("Cancel") }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import { DateTime } from "luxon";
import { mapGetters } from "vuex";
import _ from "lodash";
import { parseLocaleDate, formatLocaleDate } from "@/shared/dateFormatUtils";

function differenceInDays(date1, date2) {
  return (date2.getTime() - date1.getTime()) / (1000 * 3600 * 24);
}

/// vm should be a Vue Component
/// dateRangeDates should be string[] of 0-2 lenght
export const formatCountOfNights = (vm) => (dateRangeDates) => {
  if (dateRangeDates.length == 1) {
    return vm.$tc("{count} of nights", 0, { count: 0 });
  } else if (dateRangeDates.length == 2) {
    const nights = differenceInDays(
      new Date(dateRangeDates[0]),
      new Date(dateRangeDates[1])
    );
    return vm.$tc("{count} of nights", nights, { count: nights });
  } else {
    return "";
  }
};

export default {
  props: {
    value: {
      type: [String, Array],
      required: false,
    },

    outlined: {
      type: Boolean,
      default: true,
    },

    // NOT COMPATIBLE with range mode
    editable: {
      type: Boolean,
      default: false,
    },

    dense: {
      type: Boolean,
      default: false,
    },

    hideDetails: {
      type: Boolean,
      default: false,
    },

    clearable: {
      type: Boolean,
      default: false,
    },

    customIcon: {
      type: String,
      default: undefined,
    },

    show: {
      type: Boolean,
      default: false,
    },

    offsetY: {
      type: Boolean,
      default: true,
    },
    offsetX: {
      type: Boolean,
      default: false,
    },

    nudgeLeft: {
      type: String,
      required: false,
    },

    origin: {
      type: String,
      default: "top left",
    },

    label: {
      type: String,
      default: "Date",
    },

    hint: {
      type: String,
      required: false,
    },

    range: {
      type: Boolean,
      default: false,
    },

    shouldBeConfirmed: {
      type: Boolean,
      default: false,
    },

    autofocus: {
      type: Boolean,
      default: false,
    },

    min: {
      type: String,
      required: false,
    },

    max: {
      type: String,
      required: false,
    },

    type: {
      type: String,
      default: "date",
    },

    display: {
      type: String,
      required: false,
    },

    width: {
      type: Number,
      required: false,
    },

    btn: {
      type: Boolean,
      default: false,
    },

    activePicker: {
      type: String,
      default: "DATE",
    },

    rules: {
      type: Array,
      required: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    pickerDate: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      showMenu: false,
      oldValue: [],
      manualConfirm: false,
      internalStringValue: "",
      confirmed: [],
    };
  },

  computed: {
    ...mapGetters("language", ["language"]),

    isDisabled() {
      const fieldDisabled = this.$refs["field"]?.disabled ?? false;
      const selfDisabled = this.disabled;

      return fieldDisabled || selfDisabled;
    },

    disableConfirm() {
      return this.value ? this.value.length != 2 : true;
    },

    disableConfirmOnDatePicker() {
      return !this.value;
    },

    pickerType() {
      return this.type === "year" ? "month" : this.type;
    },

    formatedValue() {
      if (this.display) {
        return this.display;
      } else if (this.range) {
        if (this.value.length == 1) {
          return this.d(DateTime.fromISO(this.value));
        } else if (this.value.length == 2) {
          const [from, to] =
            this.value[0] < this.value[1]
              ? [
                  DateTime.fromISO(this.value[0]),
                  DateTime.fromISO(this.value[1]),
                ]
              : [
                  DateTime.fromISO(this.value[1]),
                  DateTime.fromISO(this.value[0]),
                ];

          // prettier-ignore
          const range = `${this.$t('DateField.Display', {from: this.d(from), to: this.d(to)})}`
          return range;
        } else {
          return "";
        }
      } else if (!_.isArray(this.value)) {
        return this.internalStringValue;
      } else {
        return "";
      }
    },

    appendIcon() {
      if (this.customIcon) {
        return this.customIcon;
      }
      if (this.clearable) {
        return undefined;
      }
      return "fa-calendar";
    },
  },

  watch: {
    show: {
      immediate: true,

      handler(show) {
        show = show === true || show === false ? show : false;

        this.toggleMenu(show, false);
      },
    },

    value: {
      immediate: true,
      handler(fresh) {
        // reset pick loop for range mode :P
        // i have a not-string(is-array) check here just so there is less of a chance to break somewhere
        // if it's range, it should always be an array
        if (this.range && !_.isString(fresh)) {
          this.confirmed = [...fresh].sort().map((x) => DateTime.fromISO(x));
        } else {
          this.internalStringValue = fresh
            ? this.d(DateTime.fromISO(fresh))
            : "";
        }
      },
    },

    showMenu(val) {
      val &&
        setTimeout(
          () => (this.$refs.datePicker.activePicker = this.activePicker)
        );

      if (this.show !== this.showMenu) {
        this.$emit("update:show", this.showMenu);
      }
      //when opening save value so we can later use it if user cancels input
      if (val && (this.range || this.shouldBeConfirmed)) {
        this.manualConfirm = false;
        this.oldValue = this.value;
      }
      //when closing menu (pressing outside or cancel button) check if the values are the same
      //so we don't send potentially unnecessary overbookin warnings and such
      if (
        !val &&
        !this.manualConfirm &&
        !_.isEqual(this.oldValue, this.value) &&
        this.range
      ) {
        this.$emit("input", this.oldValue);
      }
    },
  },

  methods: {
    d(val) {
      return formatLocaleDate(val, this.language);
    },

    focusDatepicker() {
      if (this.editable) return;

      const f = this.$refs.datePicker;
      f?.$el.focus();
    },

    handleFieldInput(val) {
      if (!this.editable) return;

      this.internalStringValue = val;

      // if everything is deleted, update (clear)
      if (!val || val === "") {
        this.$emit("input", val);
      }

      if (typeof this.dateFormat(val) !== "string") {
        // update if valid, otherwise the handleBlur will revert later
        const parsed = parseLocaleDate(val, this.language);
        if (parsed) {
          // ? .substr is used to produce the same format as elsewhere
          // ? alternatively .toISODate() may be appropriate
          this.$emit("input", parsed.toISO().substr(0, 10));
          this.$emit("update:picker-date", parsed.toISO().substr(0, 10));
        }
      }
    },

    handleBlur() {
      if (!this.editable) return;

      const luxObj = DateTime.fromISO(this.value?.substr(0, 10));
      this.internalStringValue = luxObj ? this.d(luxObj) : "";
    },

    keydown(e) {
      if (this.readonly || this.isDisabled) return;

      // only enter if editeble
      if (this.editable) {
        if (e.key === "Enter") {
          this.confirm(e);
        }

        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        this.confirm(e);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.up(e);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        this.down(e);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        this.left(e);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        this.right(e);
      }
    },

    toggleMenu(show = null, emit = true) {
      show = show !== null ? show : !this.showMenu;

      if (show === this.showMenu) {
        return;
      }

      this.showMenu = show;

      if (emit) {
        this.$emit("update:show", show);
      }
    },

    confirm() {
      if (!this.showMenu) {
        this.toggleMenu(true);
        return;
      }

      if (!this.range) {
        this.toggleMenu(false);
        if (this.$refs.datePicker.value) {
          this.$emit("date-selected", this.$refs.datePicker.value);
        }
      } else {
        if (this.confirmed.length === 0) {
          const date =
            this.$refs.datePicker.value.length === 0
              ? DateTime.local()
              : DateTime.fromISO(this.$refs.datePicker.value[0]);
          this.confirmed.push(date);
        } else {
          const date =
            this.$refs.datePicker.value.length === 1
              ? this.confirmed[0]
              : DateTime.fromISO(this.$refs.datePicker.value[1]);
          this.confirmed.push(date);
          this.toggleMenu(false);
          this.$emit(
            "date-selected",
            [...this.confirmed.map((x) => x.toISODate())].sort()
          );
          this.confirmed.length = 0;
        }
      }
    },

    dateSelected(e) {
      if (!this.range && !this.shouldBeConfirmed) {
        this.toggleMenu(false);
        this.$emit("date-selected", e);
      } else if (this.shouldBeConfirmed) {
        this.confirmed.push(DateTime.fromISO(e));
        this.$emit("date-selected", e);
      } else {
        if (this.confirmed.length === 2) {
          this.confirmed.length = 0;
        }
        this.confirmed.push(DateTime.fromISO(e));
        this.$emit(
          "input",
          [...this.confirmed.map((x) => x.toISODate())].sort()
        );
      }
    },

    monthSelected(e) {
      if (this.type === "month") {
        this.dateSelected(e);
      }
    },

    yearSelected(e) {
      if (this.type === "year") {
        this.dateSelected(e.toString());
      }
    },

    clamp(isodate) {
      let modified = isodate;

      if (this.min && modified < this.min) {
        modified = this.min;
      }
      if (this.max && modified > this.max) {
        modified = this.max;
      }

      return modified;
    },

    move(modifier) {
      if (!this.$refs.datePicker) return;

      if (!this.range) {
        const date = this.$refs.datePicker.value
          ? DateTime.fromISO(this.$refs.datePicker.value)
          : DateTime.local();

        const modified = this.clamp(modifier(date).toISODate());

        if (this.type === "month") {
          this.$emit("input", modified.substring(0, 7));
        } else {
          this.$refs.datePicker?.dateClick(modified);
        }
      } else {
        if (this.confirmed.length === 0) {
          const date =
            this.$refs.datePicker.value.length === 0
              ? DateTime.local()
              : DateTime.fromISO(this.$refs.datePicker.value[0]);

          const modified = this.clamp(modifier(date).toISODate());

          this.$refs.datePicker.tableDate = modified.substring(0, 7);
          this.$emit("input", [modified]);
        } else {
          const date =
            this.$refs.datePicker.value.length === 1
              ? this.confirmed[0]
              : this.confirmed[0].toISODate() === this.$refs.datePicker.value[0]
              ? DateTime.fromISO(this.$refs.datePicker.value[1])
              : DateTime.fromISO(this.$refs.datePicker.value[0]);

          const modified = this.clamp(modifier(date).toISODate());

          this.$refs.datePicker.tableDate = modified.substring(0, 7);
          this.$emit("input", [this.confirmed[0].toISODate(), modified].sort());
        }
      }
    },

    up(e) {
      if (this.readonly || this.isDisabled) return;
      if (this.editable) return;

      e.preventDefault();
      e.stopPropagation();
      if (this.type === "month") {
        this.move((m) => m.minus({ months: 3 }));
      } else {
        this.move((m) => m.minus({ weeks: 1 }));
      }
    },

    down(e) {
      if (this.readonly || this.isDisabled) return;
      if (this.editable) return;

      e.preventDefault();
      e.stopPropagation();
      if (this.type === "month") {
        this.move((m) => m.plus({ months: 3 }));
      } else {
        this.move((m) => m.plus({ weeks: 1 }));
      }
    },

    left(e) {
      if (this.readonly || this.isDisabled) return;
      if (this.editable) return;

      e.preventDefault();
      e.stopPropagation();
      this.toggleMenu(true);
      if (this.type === "month") {
        this.move((m) => m.minus({ months: 1 }));
      } else {
        this.move((m) =>
          e.shiftKey ? m.minus({ months: 1 }) : m.minus({ days: 1 })
        );
      }
    },

    right(e) {
      if (this.readonly || this.isDisabled) return;
      if (this.editable) return;

      e.preventDefault();
      e.stopPropagation();
      this.toggleMenu(true);
      if (this.type === "month") {
        this.move((m) => m.plus({ months: 1 }));
      } else {
        this.move((m) =>
          e.shiftKey ? m.plus({ months: 1 }) : m.plus({ days: 1 })
        );
      }
    },

    confirmDatePicker() {
      this.manualConfirm = true;
      this.showMenu = false;
      if (!_.isEqual(this.oldValue, this.value)) {
        this.$emit("isConfirmed", this.value);
      }
    },

    closeDatePicker() {
      if (!_.isEqual(this.oldValue, this.value)) {
        this.$emit("input", this.oldValue);
      }
      this.showMenu = false;
      this.manualConfirm = false;
    },

    dateFormat(value) {
      //remove white space for format check to make it more consistent
      value = (value ?? "").replace(/\s/g, "");
      return (
        this.range ||
        !this.editable ||
        value == undefined ||
        value.length == 0 ||
        (/(0[1-9]|[12][0-9]|3[01])[/.]([01][0-9])[/.]([0-9]{4})/g.test(value) && value.length <= 10) ||
        (/(0[1-9]|[12][0-9]|3[01])[/.]([01][0-9])[/.]([0-9]{4})[.]/g.test(value) && value.length <= 11) ||
        (/([1-9]|[12][0-9]|3[01])[/.]([1-9]|[1][012])[/.]([0-9]{4}[.]?)/g.test(value) && value.length <= 11) ||
        this.$t("Validation.WrongDateFormat")
      );
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep {
  .v-input--dense .fa-times-circle {
    margin-top: 0 !important;
  }
}
</style>